/**
 * Browser-side WebAuthn helpers for passkey registration and authentication.
 * Server options are delivered as JSON strings; responses are serialized JSON for API payloads.
 */
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

/**
 * Whether the runtime supports passkeys (WebAuthn / `PublicKeyCredential`).
 */
export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined' && window.PublicKeyCredential !== undefined;
}

/** Parses the backend `options` JSON string into a typed object for SimpleWebAuthn. */
function parseOptionsJson<T>(optionsJsonString: string): T {
  return JSON.parse(optionsJsonString) as T;
}

/**
 * Runs passkey registration using server-generated creation options.
 *
 * @param optionsJsonString - `options` field from `POST /user/passkey/register/options` (JSON string).
 * @returns Parsed registration result suitable for `attestationResponse` on complete.
 */
export async function passkeyRegisterWithOptionsString(
  optionsJsonString: string,
): Promise<RegistrationResponseJSON> {
  const optionsJSON = parseOptionsJson<PublicKeyCredentialCreationOptionsJSON>(optionsJsonString);
  return startRegistration({ optionsJSON });
}

/**
 * Runs passkey authentication using server-generated request options (2FA or passwordless).
 *
 * @param optionsJsonString - `options` from passkey options endpoints (JSON string).
 * @returns Parsed authentication result suitable for `assertionResponse` on complete.
 */
export async function passkeyAuthenticateWithOptionsString(
  optionsJsonString: string,
): Promise<AuthenticationResponseJSON> {
  const optionsJSON = parseOptionsJson<PublicKeyCredentialRequestOptionsJSON>(optionsJsonString);
  return startAuthentication({ optionsJSON });
}

/**
 * Serializes a registration result for `POST /user/passkey/register/complete` (`attestationResponse`).
 */
export function registrationResponseToAttestationJson(
  registration: RegistrationResponseJSON,
): string {
  return JSON.stringify(registration);
}

/**
 * Serializes an authentication result for passkey complete endpoints (`assertionResponse`).
 */
export function authenticationResponseToAssertionJson(
  authentication: AuthenticationResponseJSON,
): string {
  return JSON.stringify(authentication);
}

/**
 * Reads optional `transports` from the registration JSON (not always present in typings).
 * Used for `transports` on `register/complete` when the browser exposes them.
 */
export function getTransportsFromRegistration(registration: RegistrationResponseJSON): string[] {
  const t = (registration as RegistrationResponseJSON & { transports?: string[] }).transports;
  return Array.isArray(t) ? t.map(String) : [];
}
